import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import {
    // eslint-disable-next-line no-unused-vars
    collection, query, where, onSnapshot, addDoc, orderBy,
    // eslint-disable-next-line no-unused-vars
    serverTimestamp, doc, getDoc, setDoc, updateDoc, increment
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoSend, IoLocationSharp, IoPersonCircleOutline } from 'react-icons/io5';
import moment from 'moment';
import './ChatScreen.css';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function ChatScreen() {
    const { targetId } = useParams();
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [targetUser, setTargetUser] = useState(null);
    const messagesEndRef = useRef(null);

    // 1. Resolve Chat ID (Canonical: minuid_maxuid)
    const chatId = [currentUser.uid, targetId].sort().join('_');

    // 2. Fetch Target User Info
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const docRef = doc(db, "usuarios", targetId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setTargetUser(docSnap.data());
                }
            } catch (e) {
                console.error("Error fetching target user:", e);
            }
        };
        fetchUser();
    }, [targetId]);



    // ... (inside component)

    // 3. Subscribe to Messages & Clear Unread
    useEffect(() => {
        const q = query(
            collection(db, "chats", chatId, "messages"),
            orderBy("createdAt", "asc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(msgs);
            scrollToBottom();

            // Clear my unread count when messages load/update (meaning I'm reading them)
            // Use updateDoc to avoid overwriting other fields
            const chatRef = doc(db, "chats", chatId);
            // We can't use setDoc with merge for a specific field deletion/reset efficiently without touching others, 
            // but updateDoc is safer. However, doc might not exist yet if new chat.
            // But if snapshot has docs, it might exist. Actually, chat doc is created on first message.
            // If it doesn't exist, we can't update it. 
            // We'll wrap in try-catch or check existence.
            // Or simpler: setDoc with merge and unread set to 0.
            setDoc(chatRef, {
                [`unread_${currentUser.uid}`]: 0
            }, { merge: true }).catch(err => {
                // Ignore error if doc doesn't exist yet (new chat)
                console.log("Chat doc not created yet, skipping unread reset");
            });
        });

        return () => unsubscribe();
    }, [chatId, currentUser.uid]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;

        try {
            // Ensure chat document exists
            const chatRef = doc(db, "chats", chatId);

            // Increment unread count for the TARGET user
            // We use increment(1) from firestore
            // Actually, best to import `increment` at top level. I will add it to the imports in a separate block or assume it's there. 
            // Wait, I need to check imports.

            await setDoc(chatRef, {
                participants: [currentUser.uid, targetId],
                lastMessage: inputText,
                lastMessageTime: serverTimestamp(),
                [`unread_${targetId}`]: increment(1), // Increment target's unread
                participantNames: {
                    [currentUser.uid]: userData.nombre || 'Usuario',
                    [targetId]: targetUser?.nombre || 'Usuario'
                }
            }, { merge: true });

            // Add message to subcollection
            await addDoc(collection(db, "chats", chatId, "messages"), {
                text: inputText,
                senderId: currentUser.uid,
                createdAt: serverTimestamp()
            });

            setInputText('');
        } catch (error) {
            console.error("Error sending message:", error);
            alert("Error al enviar mensaje: " + error.message);
        }
    };

    const handleLocationClick = () => {
        const loc = targetUser?.location;
        if (loc && loc.lat && loc.lng) {
            // Open Google Maps with explicit marker query
            const url = `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`;
            window.open(url, '_blank');
        } else {
            alert("Ubicación no disponible en este momento. El vigilante debe tener la app abierta.");
        }
    };

    const isVigilanteViewing = userData?.rol === 'vigilante';
    const isTargetVigilante = targetUser?.rol === 'vigilante';

    // "Location button... if target is vigilante" (Resident viewing guard)
    const showLocationButton = !isVigilanteViewing && isTargetVigilante;

    return (
        <div className="chat-screen-container">
            <KondoBrandHeader showBack={false} />
            {/* Header */}
            <header className="chat-header">
                <button onClick={() => navigate(-1)} className="chat-back-btn">
                    <IoArrowBack size={24} />
                </button>

                <div className="chat-header-info">
                    {targetUser?.fotoPerfilUrl ? (
                        <img src={targetUser.fotoPerfilUrl} alt="Avatar" className="chat-avatar" />
                    ) : (
                        <div className="chat-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <IoPersonCircleOutline size={24} color="#9ca3af" />
                        </div>
                    )}
                    <div className="chat-user-details">
                        <span className="chat-user-name">{targetUser?.nombre || 'Cargando...'}</span>
                        <span className="chat-user-status">
                            {targetUser?.rol === 'vigilante' ? 'Vigilante' : 
                             targetUser?.rol === 'admin' ? 'Administrador' : 
                             targetUser?.rol === 'comite' ? 'Comité de Vigilancia' : 
                             (targetUser?.numeroCasa ? `Unidad ${targetUser.numeroCasa}` : 'Residente')}
                        </span>
                    </div>
                </div>

                {showLocationButton && (
                    <button className="btn-location" onClick={handleLocationClick} title="Ver Ubicación">
                        <IoLocationSharp />
                    </button>
                )}
            </header>

            {/* Messages */}
            <div className="chat-messages-area">
                {messages.map(msg => {
                    const isMe = msg.senderId === currentUser.uid;
                    return (
                        <div key={msg.id} className={`message-bubble ${isMe ? 'me' : 'them'}`}>
                            {msg.text}
                            <span className="message-time">
                                {msg.createdAt?.toDate ? moment(msg.createdAt.toDate()).format('h:mm A') : '...'}
                            </span>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form className="chat-input-bar" onSubmit={handleSendMessage}>
                <input
                    type="text"
                    className="chat-input"
                    placeholder="Escribe un mensaje..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                />
                <button type="submit" className="btn-send" disabled={!inputText.trim()}>
                    <IoSend />
                </button>
            </form>
        </div>
    );
}

export default ChatScreen;
